import { useCallback, useEffect, useRef, useState } from 'react';
import type { LoadedBscData } from '../types/bsc';
import { loadBscData } from '../data/source';

interface BscDataState {
  data: LoadedBscData | null;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
}

export function useBscData(): BscDataState & { reload: () => void } {
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<BscDataState>({
    data: null,
    loading: true,
    refreshing: false,
    error: null,
  });
  const hasLoaded = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    setState((previous) => ({
      ...previous,
      loading: !hasLoaded.current,
      refreshing: hasLoaded.current,
      error: null,
    }));

    loadBscData(controller.signal)
      .then((data) => {
        hasLoaded.current = true;
        setState({ data, loading: false, refreshing: false, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const normalized = error instanceof Error ? error : new Error(String(error));
        setState((previous) => ({
          ...previous,
          loading: false,
          refreshing: false,
          error: normalized,
        }));
      });

    return () => controller.abort();
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return { ...state, reload };
}
